import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Ban, MessageSquare, Video, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, errMsg, emitPostsChanged } from "@/lib/api";
import { useLiveReload } from "@/lib/live";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, PlatformRow, VideoPlayer, ThumbImg } from "@/components/Bits";
import PostDetailDialog from "@/components/PostDetailDialog";

const TABS = [
  { id: "open", label: "Offen", match: (s) => s === "pending_approval" },
  { id: "changes", label: "Änderung / Gesperrt", match: (s) => ["changes_requested", "rejected"].includes(s) },
  { id: "done", label: "Freigegeben", match: (s) => ["approved", "scheduled", "published"].includes(s) },
];

function Media({ post, file }) {
  if (!file) {
    const Icon = post.content_type === "video" ? Video : ImageIcon;
    return <div className="aspect-[4/5] grid place-items-center bg-white/5 text-slate-600"><Icon className="h-8 w-8" /></div>;
  }
  return file.kind === "video"
    ? <VideoPlayer file={file} className="aspect-[4/5] w-full object-cover bg-black" />
    : <ThumbImg file={file} className="aspect-[4/5] w-full object-cover" />;
}

export default function Approvals() {
  const { clientId } = useClient();
  const { user } = useAuth();
  const isClient = user.role === "client";
  const [posts, setPosts] = useState([]);
  const [files, setFiles] = useState([]);
  const [tab, setTab] = useState("open");
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    api.get("/posts", { params: { client_id: clientId } }).then((r) => setPosts(r.data));
    api.get("/files", { params: { client_id: clientId } }).then((r) => setFiles(r.data));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  useLiveReload(load);
  const byId = useMemo(() => Object.fromEntries(files.map((f) => [f.id, f])), [files]);

  const decide = async (p, status, msg) => {
    try { await api.post(`/posts/${p.id}/status`, { status }); toast.success(msg); load(); emitPostsChanged(); } catch (e) { toast.error(errMsg(e)); }
  };

  const list = posts.filter((p) => TABS.find((t) => t.id === tab).match(p.status));

  return (
    <div className="max-w-7xl">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-slate-400 mb-3">Freigabe</p>
      <h1 className="text-3xl sm:text-6xl font-bold tracking-tight mb-3">Deine <span className="text-teal-400">Freigaben</span></h1>
      <p className="text-slate-400 mb-10 max-w-xl">{isClient ? "Schau dir die Beiträge an und entscheide per Klick: freigeben, kommentieren oder sperren." : "Hier siehst du, was beim Kunden zur Freigabe liegt."}</p>
      <div className="flex gap-2 mb-8">
        {TABS.map((t) => {
          const n = posts.filter((p) => t.match(p.status)).length;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`approvals-tab-${t.id}`}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${tab === t.id ? "bg-white text-slate-950 border-white" : "border-border text-slate-400 hover:text-white"}`}>
              {t.label} <span className="ml-1 opacity-60">{n}</span>
            </button>
          );
        })}
      </div>
      {list.length === 0 && <p className="text-muted-foreground" data-testid="approvals-empty">Hier ist gerade nichts.</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {list.map((p) => (
          <div key={p.id} className="rounded-xl border border-border bg-card overflow-hidden flex flex-col fade-up" data-testid={`approval-card-${p.id}`}>
            <Media post={p} file={byId[p.media_ids?.[0]]} />
            <div className="p-4 flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-2"><PlatformRow platforms={p.platforms} /><StatusBadge status={p.status} /></div>
              <p className="font-medium leading-snug mb-1">{p.title || "Ohne Titel"}</p>
              <p className="text-xs text-slate-500 mb-4">{p.scheduled_at ? new Date(p.scheduled_at).toLocaleString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "Ohne Termin"}</p>
              <div className="mt-auto flex gap-2">
                {isClient && p.status === "pending_approval" && (
                  <>
                    <Button size="sm" onClick={() => decide(p, "approved", "Freigegeben")} className="flex-1 bg-teal-500 hover:bg-teal-400 text-slate-950 gap-1" data-testid={`quick-approve-${p.id}`}><Check className="h-3.5 w-3.5" />Freigeben</Button>
                    <Button size="sm" variant="outline" onClick={() => decide(p, "rejected", "Gesperrt")} className="gap-1 text-rose-400" data-testid={`quick-reject-${p.id}`}><Ban className="h-3.5 w-3.5" />Sperren</Button>
                  </>
                )}
                <Button size="sm" variant="outline" onClick={() => setOpen(p)} className="gap-1" data-testid={`open-approval-${p.id}`}><MessageSquare className="h-3.5 w-3.5" />{p.comments.length || ""}</Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {open && <PostDetailDialog post={open} onClose={() => setOpen(null)} onChanged={() => { load(); emitPostsChanged(); }} />}
    </div>
  );
}
